// 2026-11-02 (Keaton: "an easily accessible place for places ... a way to look at all of the characters, the places,
// and to generate other assets like objects, props"): a video's World. One home, three tabs:
//   Characters  the cast (CastPanel: sheets, looks, voices)
//   Places      empty sets (nobody in them) every scene set there is drawn from
//   Props       things that come back from scene to scene, each a picture of the thing on its own
// A card shows the picture, the name and the scenes using it; a tap opens it (rename, describe, pick its scenes, redraw,
// swap in a library picture, remove). "New" draws one from a description or (a place) from a scene's frame with the
// characters taken out, or takes a library picture for free. Desktop: the suite's side tab; phones: a section.
import { useEffect, useMemo, useRef, useState } from "react";
import { api, ApiError } from "../api";
import { director } from "../director";
import { onJob } from "../session";
import { bump, toast, useStore } from "../store";
import type { FormatInfo } from "../formats";
import type { Asset, Project } from "../types";
import { KIND_WORDS, scenesLine, WORLD_EVENT, type PlaceKind, type PlaceView, type PlacesView, type WorldTab } from "../world";
import { Icon, Sheet, Spinner, useLoad } from "./bits";
import { CastPanel } from "./Cast";
import { FullFrame } from "./FullFrame";
import { LibraryPick } from "./References";


const TAB_KEY = "cao.world.tab";
const loadTab = (fallback: WorldTab): WorldTab => {
  try {
    const v = localStorage.getItem(TAB_KEY);
    return v === "characters" || v === "location" || v === "prop" ? v : fallback;
  } catch {
    return fallback;
  }
};

export function WorldPanel({ p, format, onProject }: { p: Project; format: FormatInfo | null; onProject: (p: Project) => void }) {
  // 2026-11-03 (Keaton): any video can have characters, ads too (format null)
  const hasCast = true;
  const [tab, setTabState] = useState<WorldTab>(() => {
    const t = loadTab(hasCast ? "characters" : "location");
    return t === "characters" && !hasCast ? "location" : t;
  });
  const setTab = (t: WorldTab) => {
    setTabState(t);
    try {
      localStorage.setItem(TAB_KEY, t);
    } catch {
      /* this visit only */
    }
  };
  useEffect(() => {
    const on = (e: Event) => {
      const t = (e as CustomEvent<{ tab?: WorldTab }>).detail?.tab;
      if (t && (t !== "characters" || hasCast)) setTab(t);
    };
    window.addEventListener(WORLD_EVENT, on);
    return () => window.removeEventListener(WORLD_EVENT, on);
  });

  // places and props load once for both tabs (their counts sit on the tabs) and again when a picture lands
  const { data, error, loading, reload } = useLoad(() => api.places(p.id), [p.id]);
  const drawing = usePlaceJobs(p.id, reload);
  const cast = (p.brief as { characters?: unknown[] }).characters?.length ?? 0;
  const tabs: [WorldTab, string, number | null][] = [
    ...(hasCast ? [["characters", format?.voices === "hosts" ? "Hosts" : "Characters", cast] as [WorldTab, string, number]] : []),
    ["location", "Places", data ? data.locations.length : null],
    ["prop", "Props", data ? data.props.length : null],
  ];

  return (
    <section className="world" id="world" aria-labelledby="world-h">
      <header className="world-head">
        <h2 id="world-h">World</h2>
        <p className="world-lede">Who and what stays the same from scene to scene.</p>
      </header>
      <div className="world-tabs" role="tablist" aria-label="World">
        {tabs.map(([t, label, n]) => (
          <button key={t} id={`world-tab-${t}`} type="button" role="tab" aria-selected={tab === t} className={tab === t ? "is-on" : ""} onClick={() => setTab(t)}>
            {label}
            {n !== null && n > 0 && <span className="world-count">{n}</span>}
          </button>
        ))}
      </div>
      <div className="world-body" role="tabpanel">
        {tab === "characters" ? (
          <CastPanel p={p} format={format ?? { voices: "narrator" }} onProject={onProject} />
        ) : error && !data ? (
          <p className="muted">Couldn't load them. {error}</p>
        ) : !data ? (
          loading && <Spinner label="Loading" />
        ) : (
          <PlaceGrid p={p} kind={tab} data={data} drawing={drawing.filter((j) => j.kind === tab)} reload={reload} onProject={onProject} onStarted={drawing.started} />
        )}
      </div>
    </section>
  );
}

export type Drawing = { jobId: string; kind: PlaceKind; name: string };

/** The place and prop pictures being drawn for this video (frame jobs with no scene), and a reload when one lands. */
export function usePlaceJobs(projectId: string, reload: () => void): Drawing[] & { started: (d: Drawing) => void } {
  const [mine, setMine] = useState<Drawing[]>([]);
  const jobs = useStore((s) => s.jobs);
  const live = mine.filter((d) => {
    const j = jobs[d.jobId];
    return !j || j.status === "queued" || j.status === "running";
  });
  const count = useRef(live.length);
  useEffect(() => {
    if (live.length < count.current) {
      reload();
      setMine(live);
    }
    count.current = live.length;
  }, [live.length]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => setMine([]), [projectId]);
  return Object.assign(live, { started: (d: Drawing) => setMine((m) => [...m, d]) });
}

function PlaceGrid({ p, kind, data, drawing, reload, onProject, onStarted }: { p: Project; kind: PlaceKind; data: PlacesView; drawing: Drawing[]; reload: () => void; onProject: (p: Project) => void; onStarted: (d: Drawing) => void }) {
  const words = KIND_WORDS[kind];
  const list = kind === "location" ? data.locations : data.props;
  const [adding, setAdding] = useState(false);
  const [open, setOpen] = useState<string | null>(null);
  const opened = list.find((x) => x.id === open) ?? null;
  const series = list.find((x) => x.where === "series")?.seriesName;
  const empty = !list.length && !drawing.length;
  return (
    <>
      {empty ? (
        <div className="world-empty">
          <p>{words.empty}</p>
          <button type="button" className="btn btn-primary" data-action="world.new" onClick={() => setAdding(true)}>
            <Icon name="plus" size={16} /> {words.New}
          </button>
        </div>
      ) : (
        <ul className={`world-grid world-grid-${kind}`} aria-label={words.many}>
          {list.map((x) => (
            <li key={x.id}>
              <button type="button" className="world-card" onClick={() => setOpen(x.id)} aria-label={`${x.name}, ${scenesLine(x.scenes)}`}>
                <span className="world-art">{x.thumbUrl || x.url ? <img src={x.thumbUrl || x.url || ""} alt="" loading="lazy" decoding="async" /> : <Icon name="image" size={22} />}</span>
                <span className="world-name">{x.name}</span>
                <span className={`world-scenes${x.scenes.length ? "" : " is-none"}`}>{scenesLine(x.scenes)}</span>
              </button>
            </li>
          ))}
          {drawing.map((d) => (
            <li key={d.jobId}>
              <div className="world-card is-drawing" aria-live="polite">
                <span className="world-art">
                  <Spinner />
                </span>
                <span className="world-name">{d.name}</span>
                <span className="world-scenes">Drawing…</span>
              </div>
            </li>
          ))}
          <li>
            <button type="button" className="world-card world-new" data-action="world.new" onClick={() => setAdding(true)}>
              <span className="world-art">
                <Icon name="plus" size={22} />
              </span>
              <span className="world-name">{words.New}</span>
            </button>
          </li>
        </ul>
      )}
      <p className="field-hint world-hint">
        {series ? `Shared with every video in ${series}. ` : ""}A scene uses {kind === "location" ? "a place" : "a prop"} when it's set on the scene, or when its picture's description names it. Finished frames stay as they are.
      </p>
      {adding && (
        <NewPlaceSheet
          p={p}
          kind={kind}
          prices={data.prices}
          onClose={() => setAdding(false)}
          onAdded={() => {
            setAdding(false);
            reload();
          }}
          onStarted={(d) => {
            setAdding(false);
            onStarted(d);
          }}
        />
      )}
      {opened && <PlaceSheet p={p} place={opened} prices={data.prices} onClose={() => setOpen(null)} reload={reload} onProject={onProject} onStarted={onStarted} />}
    </>
  );
}

type Source = "describe" | "scene" | "picture" | "library";

export function NewPlaceSheet({ p, kind, prices, onClose, onAdded, onStarted }: { p: Project; kind: PlaceKind; prices: PlacesView["prices"]; onClose: () => void; onAdded: () => void; onStarted: (d: Drawing) => void }) {
  const words = KIND_WORDS[kind];
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [source, setSource] = useState<Source>("describe");
  const frames = useMemo(() => (p.script?.scenes ?? []).map((s, i) => ({ n: i + 1, url: p.shots[s.id]?.frame?.url ?? null })).filter((x): x is { n: number; url: string } => !!x.url), [p]);
  const [scene, setScene] = useState<number | null>(frames[0]?.n ?? null);
  const [picked, setPicked] = useState<Asset | null>(null);
  const [library, setLibrary] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const price = source === "library" ? 0 : source === "scene" || source === "picture" ? prices.fromFrame : prices.describe;
  const sources: [Source, string, string][] = [
    ["describe", "Describe it", "Drawn in the video's look from your words"],
    ...(frames.length ? [["scene", "From a scene", kind === "prop" ? "Take it out of a scene's picture, on its own" : "A scene's picture with the characters taken out"] as [Source, string, string]] : []),
    // 2026-11-03 (Keaton's report): another place's picture as the starting point ("just the cracked seabed from it")
    ["picture", "From another picture", "Change a picture you have (another place) as you describe"],
    ["library", "From your library", "A picture you already have, as it is. Free"],
  ];
  const submit = async () => {
    if (!name.trim()) return setFailure(`Give the ${words.one} a name, the way the scenes call it.`);
    if ((source === "library" || source === "picture") && !picked) return setFailure("Choose a picture first.");
    if (source === "picture" && !description.trim()) return setFailure("Say what to make from it ('just the cracked seabed, closer').");
    setBusy(true);
    setFailure(null);
    try {
      const r = await api.addPlace(p.id, {
        kind,
        name: name.trim(),
        ...(description.trim() ? { description: description.trim() } : {}),
        ...(source === "scene" && scene ? { fromScene: scene } : {}),
        ...(source === "library" && picked ? { assetId: picked.id } : {}),
        ...(source === "picture" && picked ? { fromItem: picked.id } : {}),
        ...(source !== "library" ? { expectCredits: price } : {}),
      });
      if (r.job) {
        onJob(r.job);
        bump("credits");
        onStarted({ jobId: r.job.id, kind, name: name.trim() });
        director.context(`The user tapped to draw ${kind === "prop" ? "the prop" : "the place"} "${name.trim()}"${source === "scene" ? (kind === "prop" ? ` taken out of scene ${scene}'s frame` : ` from scene ${scene}'s frame (characters taken out)`) : source === "picture" ? ` from the picture "${picked?.name}"` : ""} on the World panel (job ${r.job.id}, ${r.credits} credits). Don't start it again.`);
      } else {
        toast(`"${name.trim()}" is one of this video's ${words.many.toLowerCase()}.`, "good");
        director.context(`The user tapped to add the library picture "${picked?.name}" as the ${words.one} "${name.trim()}" on the World panel (free).`);
        onAdded();
      }
    } catch (e) {
      setFailure(e instanceof ApiError && e.code === "CONFIRM_REQUIRED" ? "The price changed. Check it and try again." : (e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  if (library)
    return (
      <LibraryPick
        p={p}
        title={`A picture for the ${words.one}`}
        exclude={[]}
        busy={false}
        onBack={() => setLibrary(false)}
        onClose={() => setLibrary(false)}
        onPick={(a) => {
          setPicked(a);
          setLibrary(false);
          if (!name.trim() && source === "library") setName(a.name.replace(/\.[a-z0-9]{2,4}$/i, "").slice(0, 60));
        }}
      />
    );
  return (
    <Sheet title={words.New} onClose={onClose} className="world-sheet">
      <form
        className="sheet-body"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <label className="field">
          <span className="field-label">Name</span>
          <input value={name} onChange={(e) => setName(e.target.value)} maxLength={60} placeholder={kind === "prop" ? "the brass lantern" : "the lighthouse room"} data-autofocus autoComplete="off" />
          <span className="field-hint">The way the scenes say it, so they find it.</span>
        </label>
        <label className="field">
          <span className="field-label">What it looks like</span>
          <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} maxLength={400} placeholder={kind === "prop" ? "Old, dented brass with a cracked glass chimney" : "Round stone walls, a brass lamp, a narrow window over the sea"} />
        </label>
        <fieldset className="world-sources">
          <legend className="field-label">Where the picture comes from</legend>
          {sources.map(([id, label, hint]) => (
            <label key={id} className={`world-source${source === id ? " is-on" : ""}`}>
              <input type="radio" name="world-source" value={id} checked={source === id} onChange={() => setSource(id)} />
              <span>
                <strong>{label}</strong>
                <span className="muted">{hint}</span>
              </span>
            </label>
          ))}
        </fieldset>
        {source === "scene" && (
          <div className="world-frames" role="radiogroup" aria-label="Which scene's picture">
            {frames.map((f) => (
              <button key={f.n} type="button" role="radio" aria-checked={scene === f.n} className={`world-frame${scene === f.n ? " is-on" : ""}`} onClick={() => setScene(f.n)}>
                <img src={f.url} alt="" loading="lazy" decoding="async" />
                <span>Scene {f.n}</span>
              </button>
            ))}
          </div>
        )}
        {(source === "library" || source === "picture") && (
          <div className="world-picked">
            {picked ? (picked.thumbUrl || picked.url) && <img src={picked.thumbUrl || picked.url || ""} alt="" /> : null}
            <button type="button" className="btn btn-quiet btn-small" onClick={() => setLibrary(true)}>
              {picked ? "Choose another" : "Choose a picture"}
            </button>
          </div>
        )}
        {failure && (
          <p className="report-failure" role="alert">
            {failure}
          </p>
        )}
        <button type="submit" className="btn btn-primary btn-block" disabled={busy}>
          {busy ? "Starting" : source === "library" ? "Add it" : `${words.draw} · ${price} credits`}
        </button>
        {source !== "library" && <p className="sheet-note">With {prices.model}. It shows up here in about a minute.</p>}
      </form>
    </Sheet>
  );
}

export function PlaceSheet({ p, place, prices, onClose, reload, onProject, onStarted }: { p: Project; place: PlaceView; prices: PlacesView["prices"]; onClose: () => void; reload: () => void; onProject: (p: Project) => void; onStarted: (d: Drawing) => void }) {
  const words = KIND_WORDS[place.kind];
  const [name, setName] = useState(place.name);
  const [description, setDescription] = useState(place.description);
  const [busy, setBusy] = useState(false);
  const [full, setFull] = useState(false);
  const [library, setLibrary] = useState(false);
  const [removing, setRemoving] = useState(false);
  const scenes = p.script?.scenes ?? [];
  const using = new Map(place.scenes.map((s) => [s.n, s.explicit]));
  const dirty = name.trim() !== place.name || description !== place.description;
  const run = async (fn: () => Promise<void>, fail: string) => {
    setBusy(true);
    try {
      await fn();
    } catch (e) {
      toast(`${fail} ${(e as Error).message}`, "bad");
    } finally {
      setBusy(false);
    }
  };
  const toggle = (n: number) =>
    run(async () => {
      const next = using.has(n) ? place.scenes.filter((s) => s.n !== n).map((s) => s.n) : [...place.scenes.map((s) => s.n), n];
      onProject(await api.setPlaceScenes(p.id, place.kind, place.id, next));
      director.context(`The user tapped to ${using.has(n) ? "take" : "put"} the ${words.one} "${place.name}" ${using.has(n) ? "out of" : "into"} scene ${n} on the World panel (from its next frame; free).`);
      reload();
    }, "Couldn't change that.");
  if (library)
    return (
      <LibraryPick
        p={p}
        title={`A new picture for ${place.name}`}
        exclude={place.assetId ? [place.assetId] : []}
        busy={busy}
        onBack={() => setLibrary(false)}
        onClose={() => setLibrary(false)}
        onPick={(a) =>
          void run(async () => {
            await api.addPlace(p.id, { kind: place.kind, name: place.name, assetId: a.id });
            director.context(`The user tapped to use the library picture "${a.name}" for the ${words.one} "${place.name}" on the World panel (free).`);
            setLibrary(false);
            reload();
          }, "Couldn't use that picture.")
        }
      />
    );
  return (
    <Sheet title={place.name} onClose={onClose} className="world-sheet">
      <div className="sheet-body">
        {full && place.url && <FullFrame src={place.url} alt="" title={place.name} onClose={() => setFull(false)} />}
        {place.url || place.thumbUrl ? (
          <button type="button" className={`world-hero world-hero-${place.kind}`} onClick={() => setFull(true)} aria-label={`${place.name}, full screen`}>
            <img src={place.thumbUrl || place.url || ""} alt="" decoding="async" />
          </button>
        ) : null}
        {place.where === "series" && <p className="sheet-note">Shared with every video in {place.seriesName}.</p>}
        <label className="field">
          <span className="field-label">Name</span>
          <input value={name} onChange={(e) => setName(e.target.value)} maxLength={60} autoComplete="off" />
        </label>
        <label className="field">
          <span className="field-label">What it looks like</span>
          <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} maxLength={400} />
        </label>
        {dirty && (
          <button
            type="button"
            className="btn btn-quiet btn-small"
            data-action="world.edit"
            disabled={busy || !name.trim()}
            onClick={() =>
              void run(async () => {
                await api.patchPlace(p.id, place.kind, place.id, { name: name.trim(), description });
                director.context(`The user renamed or re-described the ${words.one} "${place.name}"${name.trim() !== place.name ? ` to "${name.trim()}"` : ""} on the World panel.`);
                reload();
              }, "Couldn't save that.")
            }
          >
            Save
          </button>
        )}
        {scenes.length > 0 && (
          <fieldset className="world-in">
            <legend className="field-label">In scenes</legend>
            <div className="world-chips">
              {scenes.map((_, i) => {
                const n = i + 1;
                const on = using.has(n);
                return (
                  <button key={n} type="button" className={`chip world-chip${on ? " is-on" : ""}${on && !using.get(n) ? " is-named" : ""}`} aria-pressed={on} data-action="world.scenes" disabled={busy} onClick={() => void toggle(n)} title={on && !using.get(n) ? "Its picture's description names it" : undefined}>
                    {n}
                  </button>
                );
              })}
            </div>
            <span className="field-hint">Tap a scene to put it in or take it out. Dotted: the scene's description names it. Its next frame uses the change.</span>
          </fieldset>
        )}
        <div className="world-actions">
          <button
            type="button"
            className="btn btn-quiet"
            data-action="world.redraw"
            disabled={busy}
            onClick={() =>
              void run(async () => {
                const r = await api.addPlace(p.id, { kind: place.kind, name: place.name, ...(place.description ? { description: place.description } : {}), expectCredits: prices.describe });
                if (r.job) {
                  onJob(r.job);
                  bump("credits");
                  onStarted({ jobId: r.job.id, kind: place.kind, name: place.name });
                  director.context(`The user tapped to redraw the ${words.one} "${place.name}" on the World panel (job ${r.job.id}, ${r.credits} credits). Don't start it again.`);
                }
                onClose();
              }, "Couldn't start that.")
            }
          >
            <Icon name="redo" size={16} /> Redraw · {prices.describe}
          </button>
          <button type="button" className="btn btn-quiet" data-action="world.replace" disabled={busy} onClick={() => setLibrary(true)}>
            <Icon name="image" size={16} /> Use a library picture
          </button>
          {removing ? (
            <span className="world-confirm">
              <span>Remove it? Its picture stays in your library.</span>
              <button
                type="button"
                className="btn btn-danger btn-small"
                data-action="world.remove"
                disabled={busy}
                onClick={() =>
                  void run(async () => {
                    await api.removePlace(p.id, place.kind, place.id);
                    director.context(`The user removed the ${words.one} "${place.name}" on the World panel (its picture stays in the library).`);
                    bump("project");
                    reload();
                    onClose();
                  }, "Couldn't remove it.")
                }
              >
                Remove
              </button>
              <button type="button" className="btn btn-quiet btn-small" onClick={() => setRemoving(false)}>
                Keep
              </button>
            </span>
          ) : (
            <button type="button" className="btn btn-quiet world-remove" disabled={busy} onClick={() => setRemoving(true)}>
              <Icon name="trash" size={16} /> Remove
            </button>
          )}
        </div>
      </div>
    </Sheet>
  );
}
